import { supabase } from '../lib/supabase';

export const authService = {
  // Sign in with email and password
  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      })
      
      if (error) {
        if (error?.message?.includes('Invalid login credentials')) {
          return { 
            user: null, 
            error: { message: 'Invalid email or password. Please try again.' }
          }
        }
        return { user: null, error }
      }
      
      return { user: data?.user, error: null }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          user: null, 
          error: { 
            message: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
          }
        }
      }
      return { 
        user: null, 
        error: { message: 'Network error. Please try again.' }
      }
    }
  },

  // Sign up with email and password
  async signUp(email, password, metadata = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.fullName || email?.split('@')?.[0],
            role: metadata?.role || 'client'
          }
        }
      })
      
      if (error) return { user: null, error }
      
      return { user: data?.user, error: null }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        return { 
          user: null, 
          error: { message: 'Cannot connect to authentication service. Please check your network connection.' }
        }
      }
      return { 
        user: null, 
        error: { message: 'Registration failed. Please try again.' }
      }
    }
  },

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut()
      
      // Clear localStorage auth data  
      localStorage.removeItem('userRole')
      localStorage.removeItem('userName')
      
      // Clear session storage
      sessionStorage.clear()
      
      return { error }
    } catch (error) {
      return { error: { message: 'Network error during sign out.' } }
    }
  },

  // Get current session
  async getSession() {
    try {
      const { data, error } = await supabase?.auth?.getSession()
      return { session: data?.session, error }
    } catch (error) {
      return { session: null, error }
    }
  },

  // Get current user profile
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
      
      return { profile: data, error }
    } catch (error) {
      return { 
        profile: null, 
        error: { message: 'Failed to load user profile.' }
      }
    }
  },

  /**
   * Resend confirmation email to a user
   * @param {string} email - User's email address
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async resendConfirmationEmail(email) {
    try {
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      return {
        success: true,
        message: 'Confirmation email sent successfully',
      };
    } catch (error) {
      console.error('Error resending confirmation email:', error);
      return {
        success: false,
        message: error?.message || 'Failed to resend confirmation email',
      };
    }
  },

  /**
   * Delete a user from the system
   * Note: This requires proper RLS policies and CASCADE deletes on user_profiles
   * @param {string} userId - User ID to delete
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async deleteUser(userId) {
    try {
      // Delete from user_profiles (CASCADE will handle related tables)
      const { error: profileError } = await supabase
        ?.from('user_profiles')
        ?.delete()
        ?.eq('id', userId);

      if (profileError) throw profileError;

      return {
        success: true,
        message: 'User deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        message: error?.message || 'Failed to delete user',
      };
    }
  },

  /**
   * Get user email confirmation status
   * @param {string} userId - User ID to check
   * @returns {Promise<{confirmed: boolean, email: string}>}
   */
  async getUserConfirmationStatus(userId) {
    try {
      // Get user profile to check email confirmation
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, email_confirmed_at')
        ?.eq('id', userId)
        ?.single();

      if (profileError) throw profileError;

      return {
        confirmed: profile?.email_confirmed_at !== null,
        email: profile?.email,
        confirmedAt: profile?.email_confirmed_at,
      };
    } catch (error) {
      console.error('Error getting user confirmation status:', error);
      return {
        confirmed: false,
        email: null,
        confirmedAt: null,
      };
    }
  }
}